import { DownOutlined } from '@ant-design/icons';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import AppEmpty from '@nxsys/components/AppEmpty';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  InvoiceStatusEnum,
  PaymentFrequencyEnum,
  TimesheetStatusEnum,
} from '@nxsys/constants/AppEnums';
import { useGetPaymentStatistics } from '@nxsys/hooks/api/statistics';
import {
  useApproveTimesheet,
  useCancelTimesheet,
  useCancelSalary,
  useGenerateDraftPayroll,
  useGetBureauTimesheetGrouping,
  useGetTimesheetGrouping,
  useValidateDraftPayroll,
} from '@nxsys/hooks/api/timesheets';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import usePayrollSocket from '@nxsys/hooks/usePayrollSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { timesheetsAction } from '@nxsys/store/slices/timesheets/reducer/timesheetsSlice';
import { GetListTimesheetsParams } from '@nxsys/store/slices/timesheets/type';
import { GetTimesheetGroupingParams } from '@nxsys/types/api';
import {
  NewDraftPayroll,
  TimeSheetDataItem,
  TimesheetGroupingDataItem,
} from '@nxsys/types/models/timesheet';
import {
  calcGrossPay,
  getTaxWeekTaxYear,
  showCurrencyByCountry,
} from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Dropdown, Space } from 'antd';
import { ExportToCsv } from 'export-to-csv';
import { isEmpty, includes, isNumber, toNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import AppTaxWeekMonthSelect from '@nxsys/components/AppTaxWeekMonthSelect';
import { PaymentFrequencyType } from '@nxsys/types/models/Common';
import { IRELAND } from '@nxsys/store/constants/keys';
import DraftPayrollErrorModal from '../components/DraftPayrollErrorModal';
import NmwBreakdownModal from '../components/NmwBreakdownModal';
import AEPNValidationModal from '../components/AEPNValidationModal';
import RPNValidationModal from '../components/RPNValidationModal';
import HeaderListing from '../components/HeaderListing';
import SelectPayrollModal from '../components/SelectPayrollModal';
import TimesheetGroupCard from '../components/TimesheetGroupCard';
import ChangeTaxPeriodModal from '../components/ChangeTaxPeriodModal';
import { getGenerateDraftPayrollColumns } from './column';
import { resolveChangeTaxPeriodSelection } from './changeTaxPeriodSelection';
import { getTaxMonthByTaxWeek } from '@nxsys/utils/taxYear';

type Props = {
  cbAfterAction?: () => void;
  isDashboard?: boolean;
};

export type GenerateDraftPayrollRef = {
  renderTitle: () => React.ReactNode;
};

const GenerateDraftPayroll: React.FC<Props> = ({
  cbAfterAction = () => {},
  isDashboard = false,
}) => {
  const { messages, formatMessage } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const theme = useTheme();
  const { payrollCountry, user, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );

  const [isGroupTimesheet, setIsGroupTimesheet] = useState(true);
  const [selectedRows, setSelectedRows] = useState<TimeSheetDataItem[]>([]);
  const [payFrequencyValue, setPayFrequencyValue] =
    useState<PaymentFrequencyType | null>(null);
  const [taxPeriodValue, setTaxPeriodValue] = useState<number | null>(null);
  const [isSelectPayrollModalOpen, setIsSelectPayrollModalOpen] =
    useState(false);
  const [taxPeriodModal, setTaxPeriodModal] = useState<{
    isOpen: boolean;
    timesheets: TimeSheetDataItem[];
  }>({
    isOpen: false,
    timesheets: [],
  });

  const toggleTaxPeriodModal = () => {
    setTaxPeriodModal((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  };

  const {
    value: timesheetGrouping,
    loading: isTimesheetGroupingLoading,
    run: getTimesheetGrouping,
  } = useGetTimesheetGrouping();
  const {
    value: bureauTimesheetGrouping,
    loading: isBureauTimesheetGroupingLoading,
    run: getBureauTimesheetGrouping,
  } = useGetBureauTimesheetGrouping();
  const timesheetGroupingData = useMemo(
    () => (isBureau ? bureauTimesheetGrouping?.data : timesheetGrouping?.data),
    [isBureau, timesheetGrouping, bureauTimesheetGrouping],
  );

  const dispatch = useDispatch<AppDispatch>();
  const {
    loading: isGetTimesheetPending,
    timesheetsData: { list, pagination, queryParams, salariedEmployees },
  } = useSelector((state: AppState) => state.timesheetsStore);

  const mergedList = useMemo(() => {
    if (!isBureau) return list ?? [];
    const salaryList = (salariedEmployees ?? []).map(
      (item: TimeSheetDataItem) => ({
        ...item,
        isSalary: true,
        taxPeriod: queryParams?.taxPeriod ?? item.taxPeriod,
        taxYear: getTaxWeekTaxYear().taxYear,
      }),
    );
    const timesheetList = (list ?? []).map((item) => ({
      ...item,
      isSalary: false,
    }));
    return [...salaryList, ...timesheetList];
  }, [isBureau, list, salariedEmployees, queryParams]);

  const mergedTotal = useMemo(() => {
    if (!isBureau) return pagination.totalDocs;
    return (salariedEmployees?.length ?? 0) + (pagination.totalDocs ?? 0);
  }, [isBureau, salariedEmployees, pagination.totalDocs]);

  const { run: cancelTimesheet, loading: isCancelTimesheetPending } =
    useCancelTimesheet();

  const { run: cancelSalary, loading: isCancelSalaryPending } =
    useCancelSalary();

  const { run: approveTimesheet, loading: isApproveTimesheetPending } =
    useApproveTimesheet();

  const { run: validateDraftPayroll, loading: isValidateDraftPayrollPending } =
    useValidateDraftPayroll();

  const { run: generateDraftPayroll, loading: isGenerateDraftPayrollPending } =
    useGenerateDraftPayroll();

  const [generatePayrollStatus, setGeneratePayrollStatus] = useState<{
    errorPreIds: string[];
    errorBefIds: string[];
    errorPreSubIds: string[];
    errorBefSubIds: string[];
    errTimesheets: TimeSheetDataItem[];
    aepnSubmittedSubIds: string[];
  }>({
    errorPreIds: [],
    errorBefIds: [],
    errorPreSubIds: [],
    errorBefSubIds: [],
    errTimesheets: [],
    aepnSubmittedSubIds: [],
  });

  const {
    run: getPaymentStatistics,
    loading: isGetPaymentStatisticsPending,
    value: paymentStatisticsValue,
  } = useGetPaymentStatistics();
  const paymentStatisticsData = useMemo(
    () => paymentStatisticsValue?.data,
    [paymentStatisticsValue],
  );

  const [isDraftPayrollErrorModalOpen, setIsDraftPayrollErrorModalOpen] =
    useState(false);
  const toggleDraftPayrollErrorModal = useCallback(() => {
    setIsDraftPayrollErrorModalOpen(!isDraftPayrollErrorModalOpen);
  }, [isDraftPayrollErrorModalOpen]);

  const [nmwBreakdownData, setNmwBreakdownData] =
    useState<NewDraftPayroll | null>(null);
  const handleNmwBreakdownClick = useCallback((data: NewDraftPayroll) => {
    setNmwBreakdownData(data);
  }, []);

  // Ireland AEPN → RPN validation flow
  const [isAEPNValidationModalOpen, setIsAEPNValidationModalOpen] =
    useState(false);
  const [isRPNValidationModalOpen, setIsRPNValidationModalOpen] =
    useState(false);

  useEffect(() => {
    handleGetTimesheetGrouping();
    getPaymentStatistics();
  }, []);

  const handleGetTimesheetGrouping = useCallback(
    (params?: GetTimesheetGroupingParams) => {
      if (isBureau) {
        getBureauTimesheetGrouping(params ?? {});
      } else {
        getTimesheetGrouping(params ?? {});
      }
    },
    [getTimesheetGrouping, isBureau, getBureauTimesheetGrouping],
  );

  const getTimeSheetList = useCallback(
    (params: GetListTimesheetsParams) => {
      const newParams = {
        ...params,
        invoiceStatus: InvoiceStatusEnum.AWAITING_DRAFT,
      };
      if (isBureau) {
        if (!newParams.taxPeriod || !newParams.payFrequency) return;
        dispatch(
          timesheetsAction.getListBureauTimesheets({
            ...newParams,
            onlySavedRunPayroll: true,
            taxYear: getTaxWeekTaxYear().taxYear,
            noFilterByApprover: true,
          }),
        );
      } else {
        dispatch(timesheetsAction.getListTimesheets(newParams));
      }
    },
    [dispatch, isBureau],
  );

  const handleGoBack = useCallback(() => {
    setIsGroupTimesheet(true);
    handleGetTimesheetGrouping();
  }, []);

  const refetchTimesheetList = () => {
    getTimeSheetList({ ...queryParams });
    cbAfterAction();
  };

  const {
    receivedRef: socketReceivedRef,
    ensureConnected: ensureSocketConnected,
  } = usePayrollSocket({
    eventName: `generate_confirm-draft-payroll-${user?.id}`,
    onSuccess: (data) => {
      if (data?.isDataHasErrors) {
        showMessage({
          type: 'error',
          content: messages['timesheet.generateDraftPayroll.hasErrors'],
        });
      } else {
        showMessage({
          type: 'success',
          content: messages['timesheet.generateDraftPayroll.success'],
        });
      }
      refetchTimesheetList();
    },
    onError: (errMessage) => {
      showMessage({
        type: 'error',
        // Use `||` (not `??`) so an empty string from the socket also falls
        // back to the generic error instead of rendering a blank toast.
        content:
          errMessage || messages['timesheet.generateDraftPayroll.systemError'],
      });
      refetchTimesheetList();
    },
    onSettled: () => getPaymentStatistics(),
  });

  const handleSelectTimesheetGroup = useCallback(
    (data: TimesheetGroupingDataItem) => {
      setIsGroupTimesheet(false);
      getTimeSheetList({
        page: 0,
        size: 20,
        taxPeriod: data.taxPeriod,
        payFrequency: data.payFrequency,
        ...(data.payFrequency === PaymentFrequencyEnum.MONTHLY
          ? { monthConfig: getTaxMonthByTaxWeek(data.taxPeriod) }
          : {}),
      });
    },
    [],
  );

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    getTimeSheetList({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleFilter = (params: GetListTimesheetsParams) => {
    getTimeSheetList({
      ...queryParams,
      ...params,
      page: 0,
    });
  };

  const handleCancel = useCallback((list: TimeSheetDataItem[]) => {
    const timesheetItems = list.filter((item) => !item?.isSalary);
    const validItems = timesheetItems.filter(
      (item) =>
        item?.approveStatus !== TimesheetStatusEnum.CANCEL &&
        !item?.newDraftPayroll &&
        !item?.newPayroll,
    );
    const ids = validItems.map((item) => item.id);
    if (ids.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['timesheet.noValidTimesheetToCancel'],
      });
      return;
    }
    AppConfirmationDelete.open({
      title: messages['timesheet.cancel'],
      paragraph: formatMessage(
        { id: 'timesheet.cancelConfirm' },
        {
          timesheetList: (
            <span style={{ color: theme.palette.blue[500] }}>
              {ids.join(', ')}
            </span>
          ),
        },
      ),
      onConfirm: async () => {
        await cancelTimesheet(ids);
        showMessage({
          type: 'success',
          content: messages['timesheet.cancelSuccess'],
        });
        refetchTimesheetList();
      },
    });
  }, []);

  const handleCancelSalary = useCallback(
    (list: TimeSheetDataItem[]) => {
      const salaryIds = list.filter((i) => i?.isSalary).map((i) => i.id);
      if (salaryIds.length === 0) return;
      AppConfirmationDelete.open({
        title: messages['timesheet.cancel'],
        paragraph: formatMessage(
          { id: 'timesheet.cancelConfirm' },
          {
            timesheetList: (
              <span style={{ color: theme.palette.blue[500] }}>
                {salaryIds.join(', ')}
              </span>
            ),
          },
        ),
        onConfirm: async () => {
          await cancelSalary({
            subcontractorIds: salaryIds,
            payFrequency: queryParams?.payFrequency ?? '',
            taxYear: getTaxWeekTaxYear().taxYear,
            taxPeriod:
              (queryParams?.payFrequency === PaymentFrequencyEnum.MONTHLY
                ? getTaxMonthByTaxWeek(queryParams?.taxPeriod ?? 0)
                : queryParams?.taxPeriod) || 0,
          });
          showMessage({
            type: 'success',
            content: messages['timesheet.cancelSuccess'],
          });
          refetchTimesheetList();
          getPaymentStatistics();
        },
      });
    },
    [queryParams],
  );

  const handleCancelSelected = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    const timesheetItems = selectedRows.filter((i) => !i?.isSalary);
    const salaryItems = selectedRows.filter((i) => i?.isSalary);
    if (timesheetItems.length > 0) handleCancel(timesheetItems);
    if (salaryItems.length > 0) handleCancelSalary(salaryItems);
  }, [selectedRows]);

  const handleApprove = useCallback((list: TimeSheetDataItem[]) => {
    const ids = list.map((item) => item.id);
    approveTimesheet({ ids }).then(() => {
      showMessage({
        type: 'success',
        content: messages['timesheet.approveSuccess'],
      });
      refetchTimesheetList();
      setSelectedRows([]);
    });
  }, []);

  const handleApproveSelected = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleApprove(selectedRows);
  }, [selectedRows]);

  const handleOpenChangeTaxPeriod = useCallback((list: TimeSheetDataItem[]) => {
    // Salary rows are not timesheets — they have no per-timesheet tax period.
    const timesheetItems = list.filter((item) => !item?.isSalary);
    const selection = resolveChangeTaxPeriodSelection(timesheetItems);
    switch (selection.status) {
      case 'empty':
        showMessage({
          type: 'warning',
          content: messages['common.noSelected'],
        });
        return;
      case 'noneEligible':
        showMessage({
          type: 'warning',
          content: messages['timesheet.noValidTimesheetToChangeTaxPeriod'],
        });
        return;
      case 'mixedFrequency':
        showMessage({
          type: 'warning',
          content: messages['timesheet.changeTaxPeriodMixedFrequency'],
        });
        return;
      case 'ok':
        setTaxPeriodModal({ isOpen: true, timesheets: selection.selected });
        return;
    }
  }, []);

  const handleChangeTaxPeriodSelected = useCallback(() => {
    handleOpenChangeTaxPeriod(selectedRows);
  }, [selectedRows]);

  const doGenerateDraftPayroll = useCallback(
    async (timesheetList: TimeSheetDataItem[]) => {
      const selectedTimesheets = timesheetList.filter((ts) => !ts?.isSalary);
      const selectedSalaried = timesheetList.filter(
        (ts) => ts?.isSalary === true,
      );

      const canceledTimesheets = selectedTimesheets.filter(
        (ts) => ts.approveStatus === TimesheetStatusEnum.CANCEL,
      );

      if (canceledTimesheets.length > 0) {
        showMessage({
          type: 'error',
          content: formatMessage(
            {
              id: 'timesheet.generateDraftPayroll.canceledTimesheetError',
            },
            {
              canceledTimesheets: canceledTimesheets
                .map((ts) => ts.id)
                .join(', '),
            },
          ),
        });
        return;
      }

      const salariedEmployeesParam = isBureau
        ? {
            subcontractorIds: selectedSalaried.map((e) => e.id),
            taxPeriod:
              (queryParams?.payFrequency === PaymentFrequencyEnum.MONTHLY
                ? getTaxMonthByTaxWeek(queryParams?.taxPeriod ?? 0)
                : queryParams?.taxPeriod) || 0,
            payFrequency: queryParams?.payFrequency ?? '',
            taxYear: getTaxWeekTaxYear().taxYear,
          }
        : undefined;

      try {
        const resValidate = await validateDraftPayroll({
          ids: selectedTimesheets.map((ts) => ts.id),
          salariedEmployees: salariedEmployeesParam,
        });
        const {
          errorPreIds,
          errorBefIds,
          validatedIds,
          errTimesheets,
          validatedSubIds,
          errorPreSubIds,
          errorBefSubIds,
          aepnSubmittedSubIds,
        } = resValidate;

        const generateParams: any = {
          ids: validatedIds ?? [],
        };

        if (!isEmpty(validatedSubIds) && salariedEmployeesParam) {
          generateParams.salariedEmployees = {
            ...salariedEmployeesParam,
            subcontractorIds: validatedSubIds,
          };
        }

        if (!isEmpty(validatedIds) || !isEmpty(validatedSubIds)) {
          socketReceivedRef.current = false;
          ensureSocketConnected();
          await generateDraftPayroll(generateParams);
          showMessage({
            type: 'info',
            content: messages['timesheet.generateDraftPayroll.pending'],
          });
          // Fallback refetch only if socket notification is missed after 10s
          setTimeout(() => {
            if (!socketReceivedRef.current) {
              refetchTimesheetList();
              getPaymentStatistics();
            }
          }, 10_000);
        }

        setGeneratePayrollStatus({
          errorPreIds,
          errorBefIds,
          errorPreSubIds: errorPreSubIds ?? [],
          errorBefSubIds: errorBefSubIds ?? [],
          errTimesheets,
          aepnSubmittedSubIds: aepnSubmittedSubIds ?? [],
        });

        const hasErrors =
          !isEmpty(errorPreIds) ||
          !isEmpty(errorBefIds) ||
          !isEmpty(errorPreSubIds) ||
          !isEmpty(errorBefSubIds) ||
          !isEmpty(errTimesheets) ||
          !isEmpty(aepnSubmittedSubIds);

        if (hasErrors) {
          showMessage({
            type: 'error',
            content:
              "Highlighted workers contain errors and won't be proceed for payroll. Check the error column",
          });
        }
      } catch (err) {
        console.error(err);
      }
    },
    [queryParams],
  );

  const handleGenerateDraftPayroll = useCallback(
    (timesheetList: TimeSheetDataItem[]) => {
      doGenerateDraftPayroll(timesheetList);
    },
    [doGenerateDraftPayroll],
  );

  const handleGenerateDraftPayrollSelected = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleGenerateDraftPayroll(selectedRows);
  }, [selectedRows, handleGenerateDraftPayroll]);

  const renderTitle = useCallback(() => {
    return (
      <Space>
        {!isGroupTimesheet && <AppBackButton onClick={handleGoBack} />}
        <AppPageTitle
          prefix={messages['timesheet.generateDraftPayroll']}
          separator=""
          title={''}
        />
      </Space>
    );
  }, [messages, isGroupTimesheet]);

  const renderRightContent = useCallback(() => {
    const menuProps = {
      items: [
        {
          key: 'changeTaxPeriod',
          label: messages['timesheet.changeTaxPeriod'],
          onClick: () => handleChangeTaxPeriodSelected(),
        },
        {
          key: 'cancelTimesheet',
          label: messages['timesheet.cancel'],
          onClick: () => handleCancelSelected(),
        },
        {
          key: 'exportTimesheet',
          label: messages['common.export'],
          onClick: handleExportSelected,
        },
        {
          key: 'addPayment',
          label: messages['common.addPayment'],
          onClick: () => navigate('/payments'),
        },
        {
          key: 'addDeduction',
          label: messages['common.addDeduction'],
          onClick: () => navigate('/deductions'),
        },
      ],
    };

    return (
      <div
        className={`flex items-center gap-x-2 ${isDashboard ? 'mr-[48px]' : ''}`}
      >
        {isNumber(paymentStatisticsData?.totalErrorDraftPayroll) &&
          paymentStatisticsData?.totalErrorDraftPayroll > 0 && (
            <AppButton
              className="btn-delete"
              name={`${paymentStatisticsData?.totalErrorDraftPayroll} ${messages['common.errors']}`}
              variant="outlined"
              onClick={toggleDraftPayrollErrorModal}
            />
          )}
        {!isGroupTimesheet && (
          <>
            {payrollCountry === IRELAND && (
              <>
                <AppButton
                  name="Update RPN"
                  variant="outlined"
                  onClick={() => setIsRPNValidationModalOpen(true)}
                />
                <AppButton
                  name="Update AEPN"
                  variant="outlined"
                  onClick={() => setIsAEPNValidationModalOpen(true)}
                />
              </>
            )}
            <AppButton
              name={messages['timesheet.generateDraftPayroll']}
              type="primary"
              onClick={handleGenerateDraftPayrollSelected}
            />
            <Dropdown menu={menuProps}>
              <AppButton
                variant="outlined"
                name={messages['common.action']}
                rightIcon={<DownOutlined />}
              />
            </Dropdown>
          </>
        )}
      </div>
    );
  }, [
    messages,
    isGroupTimesheet,
    handleCancelSelected,
    handleApproveSelected,
    handleChangeTaxPeriodSelected,
    handleGenerateDraftPayrollSelected,
    paymentStatisticsData,
    toggleDraftPayrollErrorModal,
    isDashboard,
    payrollCountry,
  ]);

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        content: messages['common.noSelected'],
        type: 'warning',
      });
      return;
    }
    let dataExport;
    let headers;

    if (!isBureau) {
      dataExport = selectedRows.map((item) => ({
        timeSheetID: item?.id || '',
        agencyReference: `${
          item?.subcontractorRef?.primaryAgency?.agencyPrefix || '___'
        }/${item?.subcontractorRef?.agencyRef || ''}`,
        employeeName: `${item?.subcontractorRef?.forename || ''} ${
          item?.subcontractorRef?.surname || ''
        }`,
        agencyName: item?.agencyRef?.name || '',
        paymentCompany: item?.companyRef?.companyName || '',
        batch: item?.batch || '',
        timeSheetValue: formatMoney(
          toNumber(item?.totalNet || 0),
          showCurrencyByCountry(payrollCountry),
        ),
        taxPeriod: item?.taxPeriod || '',
        taxYear: item?.taxYear || item?.currentYear || '',
        weekEndDate: item?.endDate
          ? moment(item?.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
      }));
      headers = [
        'Timesheet ID',
        'Agency Reference',
        'Employee Name',
        'Agency Name',
        'Payment Company',
        'Batch',
        'Timesheet Values',
        'Tax Period',
        'Tax Year',
        'Week End Date',
      ];
    } else {
      dataExport = selectedRows.map((item) => ({
        id: item?.id,
        // Header label is "System ID" to match Old UI Payroll export,
        // but the value is still the subcontractor primary id (SR-xxx) per NX-5015.
        systemId: item?.isSalary
          ? item?.id
          : item?.subcontractorRef?.id || '',
        subcontractorName: item?.isSalary
          ? item?.fullName
          : `${item?.subcontractorRef?.forename || ''} ${item?.subcontractorRef?.surname || ''}`,
        batch: item?.batch || '',
        paymentCompany: item?.isSalary
          ? item?.companyPayment?.companyName
          : item?.companyRef?.companyName || item?.setting?.companyName || '',
        endDate: item?.endDate
          ? moment(item?.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        taxPeriod: `${item?.taxPeriod}/${item?.taxYear || item?.currentYear}`,
        totalNet: item?.isSalary
          ? item?.grossPay
            ? formatMoney(item?.grossPay, showCurrencyByCountry(payrollCountry))
            : formatMoney(
                calcGrossPay(
                  toNumber(item?.annualSalary),
                  item?.payFrequency ?? '',
                ) || 0,
                showCurrencyByCountry(payrollCountry),
              )
          : formatMoney(
              toNumber(item?.totalNet || 0),
              showCurrencyByCountry(payrollCountry),
            ),
        totalPrices: formatMoney(
          toNumber(item?.totalPrices || 0),
          showCurrencyByCountry(payrollCountry),
        ),
        annualSalary: item?.isSalary
          ? item?.grossPay
            ? formatMoney(item?.grossPay, showCurrencyByCountry(payrollCountry))
            : formatMoney(
                calcGrossPay(item?.annualSalary || 0, item?.payFrequency ?? ''),
                showCurrencyByCountry(payrollCountry),
              )
          : formatMoney(
              toNumber(item?.totalNet || 0),
              showCurrencyByCountry(payrollCountry),
            ),
      }));

      headers = [
        'ID',
        'System ID',
        'Employee Name',
        'Batch #',
        'Employer',
        'Week End Date',
        'Tax Period',
        'Net',
        'Total',
        'Gross Pay',
      ];
    }

    // Bureau (Payroll) keeps Old UI filename "Timesheet-export-";
    // Umbrella keeps the NX-5054 filename "Draft-Payroll-export-".
    const filenamePrefix = isBureau ? 'Timesheet-export' : 'Draft-Payroll-export';

    const csvExporter = new ExportToCsv({
      filename: `${filenamePrefix}-${moment(new Date())
        .tz('Europe/London')
        .format('DD_MM_YYYY')}`,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      useTextFile: false,
      useBom: true,
      headers,
    });
    csvExporter.generateCsv(dataExport);
  };

  const handleView = useCallback(
    (id: string) => {
      navigate(`/timesheets/${id}`, {
        query: { from: '/timesheets/generate-draft-payroll' },
      });
    },
    [navigate],
  );

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.startDate ||
    !!queryParams?.endDate ||
    !!queryParams?.batchFrom ||
    !!queryParams?.batchTo ||
    !!queryParams?.timesheetId ||
    (queryParams?.agencyRefs && queryParams?.agencyRefs?.length > 0) ||
    (queryParams?.companyRefs && queryParams?.companyRefs?.length > 0) ||
    !!queryParams?.paymentModel;

  const renderTable = () => (
    <AppTableContainer
      rowKey="id"
      data={mergedList}
      loading={isGetTimesheetPending}
      page={pagination.current}
      total={mergedTotal}
      pageSize={pagination.pageSize}
      scroll={{ x: 2500, y: 700 }}
      titleEmpty={messages['table.empty']}
      columns={getGenerateDraftPayrollColumns({
        messages,
        payrollCountry,
        handleCancel,
        handleCancelSalary,
        handleView,
        handleChangeTaxPeriod: handleOpenChangeTaxPeriod,
        theme,
        errTimesheets: generatePayrollStatus.errTimesheets,
        errorBefIds: generatePayrollStatus.errorBefIds,
        errorPreIds: generatePayrollStatus.errorPreIds,
        errorPreSubIds: generatePayrollStatus.errorPreSubIds,
        errorBefSubIds: generatePayrollStatus.errorBefSubIds,
        aepnSubmittedSubIds: generatePayrollStatus.aepnSubmittedSubIds,
        isBureau,
        onNmwBreakdownClick: handleNmwBreakdownClick,
      })}
      onChangePagination={onChangePagination}
      rowSelection={{
        type: 'checkbox',
        onChange: (_: React.Key[], selectedRows: any[]) => {
          setSelectedRows(selectedRows);
        },
      }}
      noDataType={isFiltering ? 'noDataFound' : 'noDataToShow'}
      rowClassName={(record: TimeSheetDataItem) => {
        const { id } = record;
        const {
          errorPreIds,
          errorBefIds,
          errorPreSubIds,
          errorBefSubIds,
          errTimesheets,
          aepnSubmittedSubIds,
        } = generatePayrollStatus;
        const hasError =
          includes(errorPreIds, id) ||
          includes(errorBefIds, id) ||
          includes(errorPreSubIds, id) ||
          includes(errorBefSubIds, id) ||
          errTimesheets.some((ts) => ts.id === id) ||
          includes(aepnSubmittedSubIds, id) ||
          includes(aepnSubmittedSubIds, record?.subcontractorRef?.id) ||
          !isEmpty(record?.newDraftPayroll?.errors);
        return hasError ? 'error-row' : '';
      }}
    />
  );

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
      loading={
        isTimesheetGroupingLoading ||
        isBureauTimesheetGroupingLoading ||
        isCancelTimesheetPending ||
        isCancelSalaryPending ||
        isApproveTimesheetPending ||
        isValidateDraftPayrollPending ||
        isGenerateDraftPayrollPending ||
        isGetPaymentStatisticsPending
      }
    >
      {!isTimesheetGroupingLoading &&
        !isBureauTimesheetGroupingLoading &&
        (isGroupTimesheet ? (
          <Space direction="vertical" size={16} className="w-full">
            {isBureau && (
              <div className="flex items-center justify-end gap-x-2">
                <AppPayFrequencySelect
                  value={payFrequencyValue}
                  onChange={(val) => {
                    setPayFrequencyValue(val ?? null);
                    setTaxPeriodValue(null);
                  }}
                />
                <AppTaxWeekMonthSelect
                  value={taxPeriodValue}
                  onChange={(val) => setTaxPeriodValue(val ?? null)}
                  disabled={!payFrequencyValue}
                  paymentFrequency={payFrequencyValue || 'WEEKLY'}
                />
                <AppButton
                  type="primary"
                  name={messages['common.select']}
                  disabled={!taxPeriodValue || !payFrequencyValue}
                  onClick={() => setIsSelectPayrollModalOpen(true)}
                />
              </div>
            )}
            {isEmpty(timesheetGroupingData) ? (
              isDashboard ? (
                <AppEmpty />
              ) : (
                <AppCard>
                  <AppEmpty />
                </AppCard>
              )
            ) : (
              timesheetGroupingData?.map((item) => (
                <TimesheetGroupCard
                  key={item.taxPeriod}
                  data={item}
                  onClick={handleSelectTimesheetGroup}
                />
              ))
            )}
          </Space>
        ) : isDashboard ? (
          <>
            <div className="mb-[20px]">
              <HeaderListing onFilter={handleFilter} isPendingApproval={true} />
            </div>
            <AppsContent>{renderTable()}</AppsContent>
          </>
        ) : (
          <AppCard>
            <AppsHeader>
              <HeaderListing onFilter={handleFilter} isPendingApproval={true} />
            </AppsHeader>
            <AppsContent>{renderTable()}</AppsContent>
          </AppCard>
        ))}
      <DraftPayrollErrorModal
        isOpen={isDraftPayrollErrorModalOpen}
        toggle={toggleDraftPayrollErrorModal}
      />
      <NmwBreakdownModal
        isOpen={!!nmwBreakdownData}
        toggle={() => setNmwBreakdownData(null)}
        data={nmwBreakdownData}
      />
      {payrollCountry === IRELAND && (
        <>
          <AEPNValidationModal
            isOpen={isAEPNValidationModalOpen}
            toggle={() => setIsAEPNValidationModalOpen(false)}
            taxPeriod={queryParams?.taxPeriod}
            payFrequency={queryParams?.payFrequency}
            companyRefs={queryParams?.companyRefs}
            search={queryParams?.search}
            sortField={queryParams?.sortField}
            sortType={queryParams?.sortType}
          />
          <RPNValidationModal
            isOpen={isRPNValidationModalOpen}
            toggle={() => setIsRPNValidationModalOpen(false)}
            taxPeriod={queryParams?.taxPeriod}
            payFrequency={queryParams?.payFrequency}
          />
        </>
      )}
      {isBureau && (
        <SelectPayrollModal
          isOpen={isSelectPayrollModalOpen}
          toggle={() => setIsSelectPayrollModalOpen(false)}
          payFrequency={payFrequencyValue ?? null}
          taxPeriod={taxPeriodValue ?? null}
          onSuccess={handleGetTimesheetGrouping}
        />
      )}
      <ChangeTaxPeriodModal
        isOpen={taxPeriodModal.isOpen}
        toggle={toggleTaxPeriodModal}
        timesheets={taxPeriodModal.timesheets}
        onSuccess={() => {
          refetchTimesheetList();
          getPaymentStatistics();
          setSelectedRows([]);
        }}
      />
    </AppsContainer>
  );
};

export default GenerateDraftPayroll;
